import React, { useEffect, useMemo, useState } from "react";

import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef, useMaterialReactTable } from "material-react-table";

import { useActionHook } from "../../../hooks";
import { useAccountSelector, useReportsActions, useReportsQueriesSelector } from "../../../store";
import { formatNumber } from "../../../utils";
import { ClientDealTableRow } from "../../../store/types";
import { SelectOption } from "../../forms";
import { TableCell } from "../Measures-table/TableCell";
import { HedgeTableToolbar } from "./HedgeFundTableToolbar";
import { tableProps, titleColumn } from "./ReportMeasuresTable";
import { TableToolbar } from "./TableToolbar";

const fmtPnl = (v: number | null): string => {
  if (v == null) return "-";
  const abs = Math.abs(v);
  let str: string;
  if (abs >= 1e9) str = (abs / 1e9).toFixed(1) + "B";
  else if (abs >= 1e6) str = (abs / 1e6).toFixed(1) + "M";
  else if (abs >= 1e3) str = (abs / 1e3).toFixed(1) + "K";
  else str = abs.toFixed(0);
  return v < 0 ? `(${str})` : str;
};

type Props = { groupByOptions: SelectOption[]; shouldFetchData: boolean };

export const useHedgeFundTable = ({ groupByOptions, shouldFetchData }: Props) => {
  const { hedge_fund } = useAccountSelector() || {};

  const [monthlyViewGroupBy, setMonthlyViewGroupBy] = useState<string>("none");
  const [monthlyViewReportBy, setMonthlyViewReportBy] = useState<"deal" | "year">("year");

  const { getHedgeFundTableActon, setIsHedgeFundsAction } = useReportsActions();
  const { filters, use_accounting_date } = useReportsQueriesSelector();

  const {
    action: getHedgeFundsTable,
    loading: hedgeFundsLoading,
    data: hedgeFundsData
  } = useActionHook((queries: Parameters<typeof getHedgeFundTableActon>[0]) =>
    shouldFetchData ? getHedgeFundTableActon(queries) : undefined
  );

  useEffect(() => {
    const reportByInvert = monthlyViewReportBy === "deal" ? "year" : "deal";
    getHedgeFundsTable({
      filters,
      groupBy: monthlyViewGroupBy === "none" ? [[reportByInvert]] : [[monthlyViewGroupBy], [reportByInvert]],
      use_accounting_date,
      rowBy: monthlyViewReportBy
    });
  }, [monthlyViewGroupBy, monthlyViewReportBy, filters, use_accounting_date, shouldFetchData]);

  const measureTableColumns = useMemo(() => {
    if (!hedgeFundsData) {
      return [];
    }
    const columns: MRT_ColumnDef<ClientDealTableRow>[] = hedgeFundsData.columns.map<MRT_ColumnDef<ClientDealTableRow>>(
      (col) => {
        const renderCell = (row: ClientDealTableRow, column: { id: string }) => {
          const raw = row.valueData[col.key] as unknown as (number | null)[] | number | null;
          const postfix = row.postfixes?.[col.key] || col.postfix || row.postfix;

          if (!Array.isArray(raw)) {
            return <TableCell id={column.id} value={raw} postfix={postfix} />;
          }

          const [monthly, pnl, ttm] = raw;
          const fmt = (v: number | null) => (v != null ? formatNumber(v) + (postfix || "") : "-");
          const valueColor = (v: number | null) => v == null || v === 0 ? "inherit" : v > 0 ? "success.main" : "error.main";
          return (
            <Stack
              alignItems="center"
              spacing={0.25}
              sx={{
                "& .hf-label": { display: "inline-block", maxWidth: 0, overflow: "hidden", opacity: 0, whiteSpace: "nowrap", transition: "max-width 0.2s ease, opacity 0.2s ease" },
                "&:hover .hf-label": { maxWidth: "40px", opacity: 1 }
              }}
            >
              <Typography sx={{ fontSize: "13px", fontWeight: 600, lineHeight: 1.3, color: valueColor(monthly) }}>
                {fmt(monthly)}
              </Typography>
              <Typography sx={{ fontSize: "10px", color: valueColor(pnl), lineHeight: 1.2, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <span className="hf-label">P&amp;L:&nbsp;</span>{fmtPnl(pnl)}
              </Typography>
              <Typography sx={{ fontSize: "10px", color: valueColor(ttm), lineHeight: 1.2, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <span className="hf-label">TTM:&nbsp;</span>{fmt(ttm)}
              </Typography>
            </Stack>
          );
        };

        return {
          header: col.title,
          id: col.key,
          accessorKey: col.key,
          sortingFn: "basic",
          accessorFn: (row) => {
            const raw = row.valueData[col.key] as unknown as (number | null)[] | number | null;
            return Array.isArray(raw) ? raw[0] ?? null : raw;
          },
          Cell: ({ row, column }) => renderCell(row.original, column),
          AggregatedCell: ({ row, column }) => renderCell(row.original, column)
        };
      }
    );

    columns.unshift(titleColumn);

    return columns;
  }, [hedgeFundsData]);

  const table = useMaterialReactTable<ClientDealTableRow>({
    // Common table Props
    ...tableProps,
    getRowId: (row) => row.id!,
    getSubRows: (row) => row.children,
    filterFromLeafRows: true,
    enableColumnActions: false,
    enableBottomToolbar: false,
    layoutMode: "grid",
    // Specific table Props
    columns: measureTableColumns,
    enableColumnResizing: true,

    data: hedgeFundsData?.rows || [],

    // This are styles for "monthly_view" to make columns be flexible
    columnResizeMode: "onEnd",
    defaultColumn: {
      ...tableProps.defaultColumn,
      size: 10, // To get rid off minimum columns size
      muiTableBodyCellProps: {
        sx: { padding: "8px 12px" }
      },
      muiTableHeadCellProps: {
        sx: { padding: "8px 12px" }
      }
    },

    enableExpanding: true,
    state: { isLoading: hedgeFundsLoading },
    renderTopToolbar: (props) => {
      return (
        <TableToolbar
          setFullScreen={() => props.table.setIsFullScreen(!props.table.getState().isFullScreen)}
          isFullScreen={props.table.getState().isFullScreen}
          {...(hedge_fund && { tabs: { value: "monthly_view", onChange: () => setIsHedgeFundsAction(false) } })}
        >
          <HedgeTableToolbar
            groupByOptions={groupByOptions}
            monthlyViewGroupBy={monthlyViewGroupBy}
            setMonthlyViewReportBy={setMonthlyViewReportBy}
            monthlyViewReportBy={monthlyViewReportBy}
            setMonthlyViewGroupBy={setMonthlyViewGroupBy}
          />
        </TableToolbar>
      );
    }
  });

  return table;
};
